import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import FormSkeleton from '../shared/FormComponents/FormSkeleton';
import { createClientSchema } from '../../utils/validation/form-schema';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import apis from '../../utils/apis';
import { FeatureTour, clientFormTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

// Individual client type IDs: parent=6, all children have parent_id=6
const INDIVIDUAL_TYPE_IDS = new Set([6, 54, 55, 41, 25, 73, 19, 63, 42]);

// All root-level type IDs that are NOT individual (used for "organization" category)
const ORGANIZATION_ROOT_IDS = new Set([1, 7, 4, 10, 11, 2, 5, 3, 9, 8]);

/** Determine if a given type ID is an individual (person) type */
function isIndividualType(typeId: number | string | null | undefined): boolean {
  if (!typeId) return true; // Default to individual
  const id = typeof typeId === 'string' ? parseInt(typeId, 10) : typeId;
  return INDIVIDUAL_TYPE_IDS.has(id);
}

/** Get the root category of a type based on its parent_id */
function getRootCategory(typeId: number | string | null | undefined, flatTypes: any[]): 'individual' | 'organization' {
  if (!typeId) return 'individual';
  const id = typeof typeId === 'string' ? parseInt(typeId, 10) : typeId;

  // Check if it's a known individual type
  if (INDIVIDUAL_TYPE_IDS.has(id)) return 'individual';

  // Check parent_id from the flat list
  const item = flatTypes.find((t: any) => t.id === id);
  if (item) {
    if (item.parent_id === 6 || item.id === 6) return 'individual';
    if (item.parent_id === null && !INDIVIDUAL_TYPE_IDS.has(id)) return 'organization';
    // Check if parent is individual
    if (item.parent_id && INDIVIDUAL_TYPE_IDS.has(item.parent_id)) return 'individual';
  }

  return 'organization';
}

interface ClientFormProps {
  onCancel?: () => void;
  onClientCreated?: (client: any) => void;
  hideHeader?: boolean;
}

const ClientForm: React.FC<ClientFormProps> = ({ onCancel, onClientCreated, hideHeader = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [clientTypeOptions, setClientTypeOptions] = useState<DropdownOption[]>([]);
  const [flatClientTypes, setFlatClientTypes] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientCategory, setClientCategory] = useState<'individual' | 'organization'>('individual');
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [formKey, setFormKey] = useState(0); // Key to force BaseForm re-render on category switch

  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: 'client-form',
    steps: clientFormTourSteps,
    readyToShow: !isLoadingData,
    autoStartDelay: 600,
  });

  // Transform flat client types into hierarchical structure
  const buildHierarchicalClientTypes = (flatList: any[]): DropdownOption[] => {
    const typeMap = new Map<number | string, DropdownOption>();

    flatList.forEach((item: any) => {
      typeMap.set(item.id, {
        value: String(item.id),
        label: item.label,
        id: item.id,
        parent_id: item.parent_id,
        sort_order: item.sort_order,
        children: [],
      });
    });

    const roots: DropdownOption[] = [];
    typeMap.forEach((option) => {
      if (option.parent_id === null) {
        roots.push(option);
      } else {
        const parent = typeMap.get(option.parent_id as number);
        if (parent && parent.children) {
          parent.children.push(option);
        }
      }
    });

    roots.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    roots.forEach((parent) => {
      if (parent.children) {
        parent.children.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      }
    });

    return roots;
  };

  // Fetch client types on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingData(true);
        const response = await apis.get('/dropdown/client-types');

        if (response && response.data) {
          let typeList = Array.isArray(response.data) ? response.data : response.data.data || response.data.list || [];
          setFlatClientTypes(typeList);
          const hierarchical = buildHierarchicalClientTypes(typeList);
          setClientTypeOptions(hierarchical);
        }
      } catch (error) {
        console.error('Error loading form data:', error);
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Error Loading Data'),
            subMsg: t('Failed to load client types. Please try again.'),
            modalType: 'error',
            duration: 5000,
          })
        );
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [dispatch]);

  // Handle client type change - detect category switch
  const handleClientTypeChange = useCallback((typeId: string) => {
    const newCategory = getRootCategory(typeId, flatClientTypes);
    const prevCategory = clientCategory;
    setSelectedTypeId(typeId);

    if (newCategory !== prevCategory) {
      setClientCategory(newCategory);
      // Force BaseForm to re-initialize with new fields
      setFormKey((prev) => prev + 1);
    }
  }, [flatClientTypes, clientCategory]);

  const handleSubmit = useCallback(
    async (formData: Record<string, any>) => {
      setIsSubmitting(true);
      try {
        const form = new FormData();

        const isIndividual = clientCategory === 'individual';

        if (isIndividual) {
          // Individual: name = "First Last"
          form.append('name', `${formData.firstName} ${formData.lastName}`.trim());
          form.append('contact_email', formData.email || '');
        } else {
          // Organization: name = company name, contact person stored separately
          form.append('name', formData.companyName || '');
          form.append('contact_email', formData.email || '');

          // Contact person fields
          const contactPersonName = `${formData.contactFirstName || ''} ${formData.contactLastName || ''}`.trim();
          if (contactPersonName) {
            form.append('contact_person_name', contactPersonName);
          }
          if (formData.contactEmail) {
            form.append('contact_person_email', formData.contactEmail);
          }
        }

        form.append('type_id', selectedTypeId || formData.clientType || '');

        if (formData.phone) {
          form.append('contact_phone', formData.phone);
        }
        if (formData.address) {
          form.append('address', formData.address);
        }
        if (formData.notes) {
          form.append('notes', formData.notes);
        }

        // Add collaborators if any are selected
        if (formData.collaborators && formData.collaborators.length > 0) {
          const collaborators = formData.collaborators
            .map((c: any) => ({
              id: c.collaborator_uuid,
              access: c.access || 'edit',
              is_lead: c.is_lead || false
            }))
            .filter((c: any) => c.id);

          if (collaborators.length > 0) {
            form.append('client_metadata', JSON.stringify({
              assignedAttorneys: collaborators,
            }));
          }
        }

        // Add files if any were selected
        if (formData.files && formData.files.length > 0) {
          for (const file of formData.files) {
            form.append('files', file);
          }
        }

        const response = await apis({
          method: 'POST',
          url: '/clients',
          data: form,
        });

        if (response && response.data && response.data.success && response.data.data) {
          const clientData = response.data.data;
          dispatch(
            showFeedbackModal({
              showModal: true,
              message: t('Client Created'),
              subMsg: t('Your client has been created successfully.'),
              modalType: 'success',
              duration: 3000,
            })
          );

          if (onClientCreated) {
            onClientCreated(clientData);
          } else {
            onCancel?.();
          }
        }
      } catch (error: any) {
        const errorMsg = error?.response?.data?.message || t('Failed to create client');
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Error Creating Client'),
            subMsg: errorMsg,
            modalType: 'error',
            duration: 5000,
          })
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, onCancel, onClientCreated, clientCategory, selectedTypeId]
  );

  // Build dynamic form sections based on client category
  const sections: BaseFormSectionConfig[] = useMemo(() => {
    const isIndividual = clientCategory === 'individual';

    const clientTypeSection: BaseFormSectionConfig = {
      title: t('Client Type'),
      description: t('Select the type of client you are creating'),
      gridCols: 1,
      fields: [
        {
          name: 'clientType',
          type: 'dropdown',
          label: t('Type'),
          placeholder: t('Select client type'),
          required: true,
          gridSize: 'full',
          customProps: {
            fullWidth: true,
            hierarchical: true,
            disableParentSelection: true,
            onSelect: (option: any) => handleClientTypeChange(option.value),
          },
          options: clientTypeOptions,
        },
      ],
    };

    const infoSection: BaseFormSectionConfig = isIndividual
      ? {
          title: t('Client Information'),
          description: t('Enter the personal details for this individual client'),
          gridCols: 2,
          fields: [
            {
              name: 'firstName',
              type: 'input',
              label: t('First Name'),
              placeholder: t('Enter first name'),
              required: true,
              gridSize: 'half',
              inputType: 'text',
            },
            {
              name: 'lastName',
              type: 'input',
              label: t('Last Name'),
              placeholder: t('Enter last name'),
              required: true,
              gridSize: 'half',
              inputType: 'text',
            },
            {
              name: 'email',
              type: 'input',
              label: t('Email'),
              placeholder: t('client@example.com'),
              required: true,
              gridSize: 'half',
              inputType: 'email',
              customProps: { fullWidth: true },
            },
            {
              name: 'phone',
              type: 'input',
              label: t('Phone (Optional)'),
              placeholder: t('+1 (555) 123-4567'),
              required: false,
              gridSize: 'half',
              inputType: 'tel',
              customProps: { fullWidth: true },
            },
            {
              name: 'address',
              type: 'input',
              label: t('Address (Optional)'),
              placeholder: t('Street, City, State, ZIP'),
              required: false,
              gridSize: 'full',
              inputType: 'text',
            },
          ],
        }
      : {
          title: t('Organization Information'),
          description: t('Enter the details for this business or organization'),
          gridCols: 2,
          fields: [
            {
              name: 'companyName',
              type: 'input',
              label: t('Company / Organization Name'),
              placeholder: t('Enter company or organization name'),
              required: true,
              gridSize: 'full',
              inputType: 'text',
            },
            {
              name: 'email',
              type: 'input',
              label: t('Company Email (Optional)'),
              placeholder: t('info@company.com'),
              required: false,
              gridSize: 'half',
              inputType: 'email',
              customProps: { fullWidth: true },
            },
            {
              name: 'phone',
              type: 'input',
              label: t('Phone (Optional)'),
              placeholder: t('+1 (555) 123-4567'),
              required: false,
              gridSize: 'half',
              inputType: 'tel',
              customProps: { fullWidth: true },
            },
            {
              name: 'address',
              type: 'input',
              label: t('Address (Optional)'),
              placeholder: t('Street, City, State, ZIP'),
              required: false,
              gridSize: 'full',
              inputType: 'text',
            },
          ],
        };

    // Contact person section (only for organizations)
    const contactPersonSection: BaseFormSectionConfig = {
      title: t('Contact Person'),
      description: t('Optionally add a primary contact person for this organization'),
      gridCols: 2,
      fields: [
        {
          name: 'contactFirstName',
          type: 'input',
          label: t('First Name'),
          placeholder: t('Contact first name'),
          required: false,
          gridSize: 'half',
          inputType: 'text',
        },
        {
          name: 'contactLastName',
          type: 'input',
          label: t('Last Name'),
          placeholder: t('Contact last name'),
          required: false,
          gridSize: 'half',
          inputType: 'text',
        },
        {
          name: 'contactEmail',
          type: 'input',
          label: t('Contact Email'),
          placeholder: t('contact@company.com'),
          required: false,
          gridSize: 'full',
          inputType: 'email',
        },
      ],
    };

    const notesSection: BaseFormSectionConfig = {
      title: t('Additional Information'),
      gridCols: 1,
      fields: [
        {
          name: 'notes',
          type: 'textarea',
          label: t('Notes (Optional)'),
          placeholder: t('Additional client information'),
          required: false,
          gridSize: 'full',
          rows: 4,
        },
      ],
    };

    const collaboratorsSection: BaseFormSectionConfig = {
      title: t('Access'),
      gridCols: 1,
      fields: [
        {
          name: 'collaborators',
          type: 'collaboratorSelector',
          label: t('Grant Access'),
          description: t('Optional - select team members to collaborate on this client'),
          required: false,
          customProps: {
            enableLeadSelection: true,
          },
        },
      ],
    };

    const attachmentsSection: BaseFormSectionConfig = {
      title: t('Attachments'),
      gridCols: 1,
      fields: [
        {
          name: 'files',
          type: 'fileUploader',
          label: t('Attachments'),
          required: false,
          maxFileSize: 52428800, // 50MB
        },
      ],
    };

    if (isIndividual) {
      return [clientTypeSection, infoSection, notesSection, collaboratorsSection, attachmentsSection];
    } else {
      return [clientTypeSection, infoSection, contactPersonSection, notesSection, collaboratorsSection, attachmentsSection];
    }
  }, [clientCategory, clientTypeOptions, t, handleClientTypeChange]);

  // Build dynamic initial values based on category
  const initialValues = useMemo(() => {
    const isIndividual = clientCategory === 'individual';

    const base: Record<string, any> = {
      clientType: selectedTypeId,
      phone: '',
      address: '',
      notes: '',
      collaborators: [],
      files: [],
      email: '',
    };

    if (isIndividual) {
      return { ...base, firstName: '', lastName: '' };
    } else {
      return { ...base, companyName: '', contactFirstName: '', contactLastName: '', contactEmail: '' };
    }
  }, [clientCategory, selectedTypeId]);

  if (isLoadingData) {
    return <FormSkeleton sections={2} fieldsPerSection={3} columns={2} />;
  }

  return (
    <div data-tour-id="client-form">
      <BaseForm
        key={formKey}
        title={t('Create New Client')}
        description={t('Fill in the details to create a new client')}
        sections={sections}
        schema={createClientSchema(clientCategory === 'individual')}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        submitLabel={t('Create Client')}
        cancelLabel={t('Cancel')}
        showConfirmationModal={true}
        isSubmitting={isSubmitting}
        hideHeader={hideHeader}
        maxWidth="max-w-4xl"
        loadingText={t('Creating client...')}
      />
      <FeatureTour
        steps={clientFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default ClientForm;
